import axios from 'axios';
import { store, createState, cancelPendingOffline } from '../store';
import { resetAnalytics } from './analytics';
import { clearUserCache, dropUserCache } from './userCache';
import { t } from '../i18n';
import { markSessionExpired } from './session_expiry';

// vue-auth owns every auth_* key. Wiping those would sign the incoming user
// straight back out, so they are the one thing a reset keeps -- dropping them on
// sign-out is vue-auth's own job, once it has used the token for the request.
const PRESERVED_KEY_PREFIX = 'auth_';

// PostHog's own state (ph_<token>_posthog and friends). Kept on a signed-out boot only: sweeping
// it there gave every sales-page visitor a new anonymous id before they signed up, so the visit
// never linked to their account. A sign-out or a different user still sweeps it, and resets.
const POSTHOG_KEY_PREFIXES = ['ph_', '__ph'];

// A bare id, and the only record of who owns the tab: drop it on sign-out and
// the next different user gets no reset at all. Always kept.
const ACTIVE_USER_KEY = 'refrag_active_user';

// Whether the tab is currently showing someone else. A flag rather than the
// impersonated id: it is all the comparison below needs, and it is nobody's
// identity. Cleared by every reset, which is what makes a stale one self-heal.
const IMPERSONATING_KEY = 'refrag_impersonating';

// RegisterPage saves a sign-up's query and hash here before the Steam round trip. Coming
// back from Steam is a signed-out boot, so that sweep has to leave it for the page to read.
const REGISTER_PENDING_KEY = 'refrag_register_pending';

// vue-auth's token key defaults, neither overridden in main.js.
const TOKEN_KEY = 'auth_token_default';
const IMPERSONATE_TOKEN_KEY = 'auth_token_impersonate';

// Both stores: vue-auth puts its tokens in sessionStorage for anyone who did not
// stay signed in.
function readToken(key) {
  return sessionStorage.getItem(key) || localStorage.getItem(key);
}

// The same signal as vue-auth's impersonating(), read from storage: main.js
// keeps the auth instance to itself, and plain modules have no handle on it.
export function isImpersonating() {
  return Boolean(readToken(IMPERSONATE_TOKEN_KEY));
}

function keepsKey(key, preserved, keepPosthog) {
  return key.startsWith(PRESERVED_KEY_PREFIX)
    || (keepPosthog && POSTHOG_KEY_PREFIXES.some((prefix) => key.startsWith(prefix)))
    || preserved.includes(key);
}

// Rather than enumerate what to remove -- a list that rots as soon as someone
// adds to it -- remove everything and name the few things to keep.
function sweep(storage, preserved, keepPosthog) {
  Object.keys(storage)
    .filter((key) => !keepsKey(key, preserved, keepPosthog))
    .forEach((key) => storage.removeItem(key));
}

function clearStorage(kept, { keepPosthog = false } = {}) {
  const preserved = [...kept, ACTIVE_USER_KEY];

  sweep(localStorage, preserved, keepPosthog);
  sweep(sessionStorage, preserved, keepPosthog);

  // vuex-persistedstate mirrors on every mutation, so leaving the store as it is
  // would write it straight back into the sessionStorage just cleared.
  cancelPendingOffline();
  store.replaceState(createState());

  // Re-read from the storage just cleared, so they hold what a fresh boot would
  // put there (see main.js).
  axios.defaults.headers.common['X-GAME'] = store.getters.selectedGame;
  axios.defaults.headers.common['X-TEAM-ID'] = localStorage.activeTeamId;
}

// Signing out is a router push, not a page load, so without this everything the
// user leaves behind sits in the tab until it closes -- readable in devtools by
// whoever uses the machine next, no sign-in needed.
//
// latch: false where no /me can be in flight to write the cache back (a boot
// with no token); latching there would silence the cache for the whole page
// load, including the sign-in that follows.
export function clearSignedOutUser({ latch = true } = {}) {
  if (latch) {
    clearUserCache();
    // A real sign-out: the next person on this browser starts anonymous in PostHog. Not on
    // a signed-out boot (latch: false), which is how a sales-page visitor arrives.
    resetAnalytics();
  } else {
    dropUserCache();
  }

  clearStorage(latch ? [] : [REGISTER_PENDING_KEY], { keepPosthog: !latch });
}

// Signing out mid-impersonation ends the admin's own session too: _processLogout
// drops auth_token_default alongside the impersonation token, while the server
// only revoked the impersonation client. Asked by every sign-out button an
// impersonated session can reach.
export function confirmSignOut(username) {
  if (!isImpersonating()) return true;

  return confirm(t('shared.session.confirm_sign_out_impersonating', {
    username: username || t('shared.session.another_user'),
  }));
}

// vue-auth logs out on any 401, but _processLogout only removes the auth_* keys
// and redirects. Without this, a session that dies server-side leaves the user on
// /login with their profile and store still in the tab.
//
// A failed sign-in is a 401 too (devise_token_auth renders 401 for bad
// credentials) and has no session behind it. The token tells the two apart,
// which is why this is registered ahead of vue-auth's own interceptor: that one
// removes the token before we would see it.
export function clearOnUnauthorized(error) {
  if (error?.response?.status === 401 && readToken(TOKEN_KEY)) {
    // A 401 on the logout request itself is a deliberate logout of a dead session, not an expiry
    if (!/auth\/sign_out/.test(error.config?.url ?? '')) markSessionExpired();
    clearSignedOutUser();
  }

  return Promise.reject(error);
}

// A different user inherits nothing at all.
export function resetClientState() {
  // The previous user's PostHog identity goes too, or the new one is identified on top of it.
  resetAnalytics();
  clearStorage([]);
}

// Called on every successful /me, the only point that knows who is signed in.
// The reload is what reaches module-level state, and it is safe because the
// auth_* keys survive the reset.
export function enforceActiveUser(user) {
  const id = String(user?.id ?? '');

  if (!id) return;

  const impersonating = isImpersonating();
  const wasImpersonating = localStorage.getItem(IMPERSONATING_KEY) === '1';

  // Starting or stopping swaps whose data the tab shows, so it takes the same
  // reset -- but auth_token_default still holds the admin's token, so the tab is
  // still theirs: same-user sweep, owner untouched. Recording the impersonated
  // user here would charge the admin a reset on their next sign-in, for someone
  // who never left.
  if (impersonating !== wasImpersonating) {
    clearStorage([]);

    if (impersonating) localStorage.setItem(IMPERSONATING_KEY, '1');

    window.location.reload();

    return;
  }

  // Already reset for this impersonation, and no /me it answers owns the tab, so
  // there is nothing to compare and nothing to record.
  if (impersonating) return;

  const previous = localStorage.getItem(ACTIVE_USER_KEY);

  if (previous === id) return;

  if (previous) resetClientState();

  localStorage.setItem(ACTIVE_USER_KEY, id);

  if (previous) window.location.reload();
}
